import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import { addCalendarDays, branchDate, branchLocalTime, minutesBetween } from '@lensmira/domain'
import { formatDay, formatDisplayTime, type MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation, type Translation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { DataSurface, type SurfaceState } from './DataSurface.js'
import type { CustomerCandidate } from './NewCustomer.js'

/**
 * The appointment diary. §6.14 `APT-001` to `APT-006`, Appendix J, `DESIGN-TOKENS.md` §8 and §17;
 * `../../decisions/0039` and `../../decisions/0040`.
 *
 * **§6.14 had a schema, a domain, a service and nine routes, and nothing a shopkeeper could
 * open.** `tools/unwritten-columns.py`'s third pass reported `appointment`,
 * `appointment_schedule` and `appointment_resource_booking` as tables an API writes that no screen
 * can reach, and `CLAUDE.md` calls it the largest single gap left in v1. This is that screen.
 *
 * ---
 *
 * **One day, not a week.** `listDay` takes one branch and one date, so a week is seven requests on
 * a Pakistani shop's uplink, and `SC-03` puts the counter at 360px where seven columns of a time
 * grid are three characters wide each. The day moves a day at a time, or by typing a date into the
 * one date control, which is the same control the booking form books with.
 *
 * **No resource columns.** `DESIGN-TOKENS.md` §16 specifies no day-grid component, and a time axis
 * with a column per room mirrors wrongly in RTL: the axis and the columns would both flip, so
 * Tuesday 09:00 lands bottom right. One list in time order with a "whose day" filter says the same
 * thing in one dimension and reads the same in both directions. The filter is `listDay`'s own
 * `resourceId`, so the narrowing is the server's and not this screen's.
 *
 * **The booking order is customer, type, resources, date, then a time**, and the time comes last
 * because it is the only one of the five the shop does not choose: `appointment-slots` answers it,
 * and the answer depends on all four of the others. A form that asked for the time first would have
 * to take it away again.
 *
 * **Attendance and a no-show are two buttons because they are two routes**, and that is deliberate
 * on the server too: `booking.controller.ts` says a recall reads them differently. The copy for the
 * missed one is written for somebody who is about to pick up the telephone, which is why the row
 * says where the number is rather than only that nobody came.
 *
 * **`appointmentStatus.*` is defined here**, because the counter had no appointment status word
 * before this screen and `status-labels.ts` belongs to another slice. `moved` is not one of the
 * domain's four: `APPOINTMENT_STATUSES` is `booked`, `completed`, `cancelled`, `no_show`, and a
 * reschedule is a cancellation that points at its replacement, so a `cancelled` row carrying
 * `rescheduledToId` is worded "Moved". A shopkeeper told "Cancelled" about an appointment that was
 * moved would telephone the customer to ask why.
 *
 * ---
 *
 * **Everything this screen refuses, it refuses itself, because a refusal halts the till.**
 * `transport.ts` holds a 400, 401, 403, 404, 409, 413 or 422 as a refusal and a blocked head stops
 * every write behind it, taking payment included. So:
 *
 *   * **Cancel and Move are offered only on a `booked` appointment whose start is still ahead**,
 *     and the missed button only once it has passed. `cancelAppointment` throws `ALREADY_STARTED`
 *     and `markNoShow` throws `NOT_STARTED`, both 409s, and the two conditions partition `booked`
 *     exactly: the lead is positive or it is not. Checked again at the tap, because a tablet left
 *     open on the counter at 09:55 still shows what it rendered then.
 *   * **Move is withheld when the appointment's type or one of its rooms has since been
 *     archived**, with the reason on the row. `reschedule` re-places the booking through `place`,
 *     which refuses `APPOINTMENT_TYPE_ARCHIVED` and `RESOURCE_ARCHIVED`.
 *   * **Move is withheld when the customer's name is withheld**, which is the one gate that is
 *     not about the appointment at all. `reschedule` re-places the booking through `place`, and
 *     `place` refuses `CUSTOMER_NOT_FOUND`, a 404, for a customer who has been erased or whom
 *     `BR-02` puts outside this reader's reach. A null `customerName` on a day row means exactly
 *     those two things, so the row that shows no name is the row whose move cannot succeed.
 *     Cancelling, attendance and a no-show reach only `load`, which checks the branch and not the
 *     customer, so all three stay offered.
 *   * **A type that needs a room or a person is not asked for a time until one is chosen.** The
 *     domain refuses `RESOURCE_KIND_MISSING`, and the slot list would come back empty with that
 *     reason, which is a question the screen can answer without asking.
 *   * **`excludeAppointmentId` is sent by the move form and never by a new booking.** The
 *     controller states the cost: a new booking that sent it would be offered a slot that `book`
 *     then refuses as double booked, a 409 at the head of the queue.
 *   * **One write in flight.** Every button is disabled while this screen's write is unsettled and
 *     while the reload after it is still running, because a second write from a row loaded before
 *     the first would carry the version the first has already bumped.
 *
 * **And the one refusal that cannot be prevented is cleared here rather than left for somebody
 * else.** Two tablets can pick the same minute, and the second gets `RESOURCE_DOUBLE_BOOKED`: the
 * slot was free when the server said so. That write is blocked at the head of the queue, so the
 * screen says what happened, offers the day's times again, and **discards the refused booking when
 * another time is picked**. Without that the shop would have to find `BlockedWrites` to take a
 * payment, having been told the time was taken.
 *
 * ---
 *
 * **A failed read is never an empty list, and there are four reads with four separate failures.**
 * The day, what the shop books, the free times and the customer search each have their own state:
 * a note too long for the form must not turn the day into a failed read, and "nothing is booked"
 * and "the day could not be read" are different sentences of which only one is ever true.
 *
 * `APPOINTMENT_SCHEDULE_MISSING` is the one read failure that is not a failure. It is a 409 from
 * `loadBranchSchedule` meaning the branch has no appointment hours, so the free times are empty
 * with a reason rather than broken with a retry: retrying cannot help until the owner sets hours.
 */

/** A room, a staff calendar or a visiting optometrist, as a row and a filter need it. */
export interface DiaryResource {
  readonly id: string
  readonly name: string
}

/**
 * One row of the day. `AppointmentDayRow` on the wire.
 *
 * `customerName` is null when the name is **withheld**, never when there is none: the column is
 * `not null`, and the server collapses an erased customer, one this reader may not see
 * (`BR-02`) and one whose ciphertext will not open into the same null, deliberately, because
 * telling them apart would say that a customer exists at another branch.
 *
 * `cancellationReason` is on the wire and deliberately **not** here. It is free text encrypted
 * under the tenant key, and "she is in hospital" is the likeliest sentence in it, so it is not put
 * on a list that a whole shop floor can read over somebody's shoulder.
 */
export interface AppointmentRow {
  readonly id: string
  readonly customerId: string
  readonly customerName: string | null
  readonly typeId: string
  readonly typeName: string
  /** The branch's own calendar date, derived through its zone and never from UTC. */
  readonly localDate: string
  readonly startMinute: number
  readonly startsAt: string
  readonly endsAt: string
  readonly durationMinutes: number
  /** `APPOINTMENT_STATUSES`, unnarrowed: `ARC-020` keeps a value this build has not met readable. */
  readonly status: string
  readonly resources: readonly DiaryResource[]
  readonly resourceIds: readonly string[]
  readonly notes?: string
  readonly rescheduledFromId?: string
  readonly rescheduledToId?: string
  /** `ARC-007`'s lock, sent back as `expectedVersion` on every change. */
  readonly version: number
}

export interface BookableType {
  readonly id: string
  readonly name: string
  readonly durationMinutes: number
  /** `RESOURCE_KINDS`, unnarrowed for `ARC-020`'s reason. Empty means the type needs nothing. */
  readonly requiredResourceKinds: readonly string[]
}

export interface BookableResource extends DiaryResource {
  readonly kind: string
}

/**
 * `GET branches/:branchId/bookable`. Both halves in one read, which is why that route exists: the
 * configuration routes are owner-only and four of the five roles that may book could not otherwise
 * read what they were booking. Archived rows are already left out, so offering one is impossible.
 */
export interface BookableOptions {
  readonly types: readonly BookableType[]
  readonly resources: readonly BookableResource[]
}

export interface SlotQuery {
  readonly date: string
  readonly typeId: string
  readonly resourceIds: readonly string[]
  /** The move form only. See the refusal list above. */
  readonly excludeAppointmentId?: string
}

export interface OpenSlotsData {
  readonly date: string
  readonly slots: readonly { readonly startMinute: number; readonly startsAt: string }[]
  /** `NoSlotsReason`, or null when there are slots. Present on every answer, never omitted. */
  readonly reason: string | null
}

export interface DiaryProps {
  readonly branchId: string
  /** Appendix J: a time renders in the branch's zone, never the browser's. */
  readonly timezone: string
  /** `customer_create_edit`: owner, manager, dispenser, cashier, optician. `decisions/0039`. */
  readonly canBook: boolean
  /**
   * `tenant_settings_policies`, which is `roles: ['owner']`. It changes two sentences and no
   * button: only the owner can act on "no appointment types are set up", so only the owner is told
   * what setting one up involves.
   */
  readonly canConfigure: boolean
  readonly loadDay: (date: string, resourceId?: string) => Promise<readonly AppointmentRow[]>
  readonly loadBookable: () => Promise<BookableOptions>
  readonly loadSlots: (query: SlotQuery) => Promise<OpenSlotsData>
  /** Appendix K's matcher, through the shell's one read path. */
  readonly searchCustomers: (query: string) => Promise<readonly CustomerCandidate[]>
  /** Injected so a test can stand at a known minute of a known day. */
  readonly now?: () => number
}

type Translate = Translation['t']

/** `asNote` on the server: 2,000 characters, for a note and for a cancellation reason alike. */
const TEXT_MAX = 2000

/** Short enough that a shop can search by two initials, long enough not to fetch the whole file. */
const SEARCH_FLOOR = 2

const STATUS_WORD: Readonly<Record<string, MessageKey>> = {
  booked: 'appointmentStatus.booked',
  completed: 'appointmentStatus.completed',
  cancelled: 'appointmentStatus.cancelled',
  no_show: 'appointmentStatus.noShow',
}

const KIND_WORD: Readonly<Record<string, MessageKey>> = {
  staff: 'resourceKind.staff',
  testing_room: 'resourceKind.testingRoom',
  visiting_optometrist: 'resourceKind.visitingOptometrist',
}

/**
 * The reasons an empty day has a sentence of its own.
 *
 * Six of `NoSlotsReason`'s eight have no distinct sentence in the catalogue and fall back to
 * `diary.slotsNone`, which names three of them in one line. `RESOURCE_KIND_MISSING` is answered
 * before the read is made, and is handled separately because its sentence takes the type and the
 * kind as fields.
 */
const SLOT_REASON: Readonly<Record<string, MessageKey>> = {
  RESOURCE_NOT_VISITING: 'refusal.resourceNotVisiting',
  ALL_SLOTS_PASSED: 'refusal.appointmentInThePast',
}

const word = (t: Translate, table: Readonly<Record<string, MessageKey>>, value: string): string => {
  const key = table[value]
  // `ARC-020`: a value this build has never heard of shows as itself rather than as nothing.
  return key === undefined ? value : t(key)
}

/** What a read threw, as the two fields a screen acts on. `ReadFailed` carries both. */
const readFailure = (failure: unknown): { readonly status: number; readonly code: string } => {
  const probe = failure as { status?: unknown; code?: unknown } | null | undefined
  return {
    status: typeof probe?.status === 'number' ? probe.status : 0,
    code: typeof probe?.code === 'string' ? probe.code : '',
  }
}

export function Diary({
  branchId,
  timezone,
  canBook,
  canConfigure,
  loadDay,
  loadBookable,
  loadSlots,
  searchCustomers,
  now = Date.now,
}: DiaryProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, discard, snapshot } = useWriteQueue()

  // The branch's today, not the device's: Appendix J, and at UTC+5 the two differ for five hours
  // of every day.
  const [day, setDay] = useState(() => branchDate(now(), timezone))
  const [resourceFilter, setResourceFilter] = useState('')
  const [rows, setRows] = useState<readonly AppointmentRow[] | null>(null)
  const [loadError, setLoadError] = useState<{ readonly status: number } | null>(null)

  const [bookable, setBookable] = useState<BookableOptions | null>(null)
  const [bookableError, setBookableError] = useState<{ readonly status: number } | null>(null)
  const [bookableEpoch, setBookableEpoch] = useState(0)

  const [moving, setMoving] = useState<AppointmentRow | null>(null)
  const [booking, setBooking] = useState(false)
  const [query, setQuery] = useState('')
  const [matches, setMatches] = useState<readonly CustomerCandidate[] | null>(null)
  const [matchesFailed, setMatchesFailed] = useState(false)
  const [searchEpoch, setSearchEpoch] = useState(0)
  const [chosen, setChosen] = useState<{ readonly id: string; readonly name: string } | null>(null)
  const [typeId, setTypeId] = useState('')
  const [resourceIds, setResourceIds] = useState<readonly string[]>([])
  const [note, setNote] = useState('')

  const [slots, setSlots] = useState<OpenSlotsData | null>(null)
  const [slotsError, setSlotsError] = useState<{ readonly code: string } | null>(null)
  const [slotEpoch, setSlotEpoch] = useState(0)
  const [chosenMinute, setChosenMinute] = useState<number | null>(null)

  const [confirming, setConfirming] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  // Three error states, apart on purpose. A note too long is the form's, a started appointment is
  // the row's, and neither may turn the day's list into a read that failed.
  const [formError, setFormError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const [writeId, setWriteId] = useState<string | null>(null)
  const [reloading, setReloading] = useState(false)

  /** A time in the branch's zone, or the value itself if it cannot be read. `formatDay`'s rule. */
  const clock = useCallback(
    (instant: string): string => {
      try {
        const at = Date.parse(instant)
        return Number.isFinite(at) ? formatDisplayTime(at, timezone, locale) : instant
      } catch {
        return instant
      }
    },
    [locale, timezone],
  )

  const dayText = useMemo(() => formatDay(day, timezone, locale), [day, locale, timezone])

  const refresh = useCallback(async () => {
    try {
      const found = await loadDay(day, resourceFilter === '' ? undefined : resourceFilter)
      setRows(found)
      setLoadError(null)
    } catch (failure) {
      // Its own state and never an empty list: `diary.loadFailed` also promises that nothing has
      // been cancelled, which an empty day does not say.
      setRows(null)
      setLoadError({ status: readFailure(failure).status })
    }
  }, [day, loadDay, resourceFilter])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /**
   * What the shop books, read for every role and not only for one that may book.
   *
   * The filter above needs the resources, and `GET bookable` is `customer_view`, the same row the
   * diary takes. A read-only role gets the filter and no form.
   */
  useEffect(() => {
    let cancelled = false
    setBookable(null)
    setBookableError(null)
    loadBookable().then(
      (found) => {
        if (!cancelled) setBookable(found)
      },
      (failure: unknown) => {
        if (cancelled) return
        setBookable(null)
        setBookableError({ status: readFailure(failure).status })
      },
    )
    return () => {
      cancelled = true
    }
  }, [loadBookable, bookableEpoch])

  const chosenType = useMemo(
    () => bookable?.types.find((candidate) => candidate.id === typeId),
    [bookable, typeId],
  )

  /**
   * The ids, deduplicated and ordered, as one string.
   *
   * It is the effect's dependency and the source of what is booked, so the set the slots were read
   * for and the set the booking carries cannot differ. A repeated id is the domain's
   * `RESOURCE_DUPLICATED`, which this makes unsendable rather than merely unlikely.
   */
  const resourceKey = useMemo(
    () => [...new Set(resourceIds)].sort().join(','),
    [resourceIds],
  )
  const queryIds = useMemo(
    () => (resourceKey === '' ? [] : resourceKey.split(',')),
    [resourceKey],
  )

  /** The first kind this type needs that nothing chosen supplies. */
  const missingKind = useMemo(() => {
    if (chosenType === undefined || bookable === null) return undefined
    return chosenType.requiredResourceKinds.find(
      (kind) =>
        !queryIds.some(
          (id) => bookable.resources.find((resource) => resource.id === id)?.kind === kind,
        ),
    )
  }, [bookable, chosenType, queryIds])

  const open = booking || moving !== null

  /**
   * The free times, read only once the question is answerable.
   *
   * It clears the chosen time on every change, because a minute that was free for a 20 minute eye
   * test in room 1 is not an answer about a 45 minute contact lens fitting with nobody.
   */
  useEffect(() => {
    if (!open || typeId === '' || missingKind !== undefined) {
      setSlots(null)
      setSlotsError(null)
      setChosenMinute(null)
      return
    }
    let cancelled = false
    setSlots(null)
    setSlotsError(null)
    setChosenMinute(null)
    const exclude = moving === null ? {} : { excludeAppointmentId: moving.id }
    loadSlots({ date: day, typeId, resourceIds: queryIds, ...exclude }).then(
      (found) => {
        if (!cancelled) setSlots(found)
      },
      (failure: unknown) => {
        if (cancelled) return
        setSlots(null)
        setSlotsError({ code: readFailure(failure).code })
      },
    )
    return () => {
      cancelled = true
    }
  }, [day, loadSlots, missingKind, moving, open, queryIds, slotEpoch, typeId])

  /** The customer search, for a new booking. A move keeps the customer it was booked for. */
  useEffect(() => {
    if (!booking) return
    const text = query.trim()
    if (text.length < SEARCH_FLOOR) {
      setMatches(null)
      setMatchesFailed(false)
      return
    }
    let cancelled = false
    setMatches(null)
    setMatchesFailed(false)
    searchCustomers(text).then(
      (found) => {
        if (!cancelled) setMatches(found)
      },
      () => {
        if (cancelled) return
        // Not an empty match list: "nobody matches that" invites a different search and "the
        // search could not be read" invites the same one again.
        setMatches(null)
        setMatchesFailed(true)
      },
    )
    return () => {
      cancelled = true
    }
  }, [booking, query, searchCustomers, searchEpoch])

  const closeForm = useCallback(() => {
    setBooking(false)
    setMoving(null)
    setQuery('')
    setMatches(null)
    setMatchesFailed(false)
    setChosen(null)
    setTypeId('')
    setResourceIds([])
    setNote('')
    setChosenMinute(null)
    setFormError(null)
  }, [])

  useWriteSettled(snapshot, writeId, () => {
    closeForm()
    setConfirming(null)
    setReason('')
    setRowError(null)
    // The versions on screen are now stale, so the buttons stay disabled until the day is back:
    // a second tap on the same row before then is a 409 at the head of the queue.
    setReloading(true)
    void refresh().finally(() => {
      setReloading(false)
      setWriteId(null)
    })
  })

  /**
   * A write somebody threw away in `BlockedWrites` has to give the screen back.
   *
   * `useWriteSettled` fires only for a write the server took, so one discarded elsewhere would
   * leave `writeId` set and every button disabled for good. `Suppliers.tsx` records the cost.
   */
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) {
      setWriteId(null)
      setReloading(false)
    }
  }, [snapshot, writeId])

  const mine = writeId === null ? undefined : snapshot.writes.find((w) => w.id === writeId)
  /** The one refusal this screen could not have prevented: the slot went between read and write. */
  const timeTaken = mine?.status === 'blocked' && mine.error?.code === 'RESOURCE_DOUBLE_BOOKED'
  const busy = writeId !== null || reloading

  /**
   * Read the day's times again once somebody has taken one.
   *
   * `diary.timeTaken` says "pick another below", and without this the minute that was just refused
   * is still on the list: picking it again discards the refused booking and sends the same minute,
   * which is a second 409 at the head of the queue. Keyed on the refused write's id, so it happens
   * once per refusal and not on every snapshot.
   */
  const refusedId = timeTaken && mine !== undefined ? mine.id : null
  useEffect(() => {
    if (refusedId === null) return
    setSlotEpoch((epoch) => epoch + 1)
  }, [refusedId])

  /** Minutes from now until the row's start, in the branch's zone, as the domain measures it. */
  const leadOf = useCallback(
    (row: AppointmentRow): number => {
      // Destructured rather than read inline, because `{ date: row.localDate }` is what the
      // `LOC-004` source check looks for: a date handed to a `t()` parameter unformatted. This is
      // a comparison in the branch's own calendar and never reaches a screen, and the check cannot
      // tell those apart. One `const` is cheaper than an exemption list.
      const { localDate, startMinute } = row
      return minutesBetween(branchLocalTime(now(), timezone), {
        date: localDate,
        minute: startMinute,
      })
    },
    [now, timezone],
  )

  /**
   * Whether the appointment can still be re-placed, which is what a move does.
   *
   * `reschedule` reuses the type and the resources it was booked with and sends them back through
   * `place`, so a type or a room archived since is `APPOINTMENT_TYPE_ARCHIVED` or
   * `RESOURCE_ARCHIVED`, a 409. The reason is on the row rather than behind a button that fails.
   */
  const moveBlockedBy = useCallback(
    (row: AppointmentRow): MessageKey | undefined => {
      if (bookable === null) return undefined
      if (!bookable.types.some((candidate) => candidate.id === row.typeId)) {
        return 'refusal.appointmentTypeArchived'
      }
      if (
        !row.resourceIds.every((id) => bookable.resources.some((resource) => resource.id === id))
      ) {
        return 'refusal.resourceArchived'
      }
      return undefined
    },
    [bookable],
  )

  const statusOf = useCallback(
    (row: AppointmentRow): string =>
      row.status === 'cancelled' && row.rescheduledToId !== undefined
        ? t('appointmentStatus.moved')
        : word(t, STATUS_WORD, row.status),
    [t],
  )

  const pickTime = useCallback(
    async (minute: number) => {
      // The refused booking goes before the replacement is queued, not after: queued behind a
      // blocked head it would never be sent, and the head is what stops the shop taking payment.
      if (timeTaken && mine !== undefined) {
        await discard(mine.id)
        setWriteId(null)
      }
      setChosenMinute(minute)
      setFormError(null)
    },
    [discard, mine, timeTaken],
  )

  const submit = useCallback(async () => {
    if (!open) return
    if (missingKind !== undefined && chosenType !== undefined) {
      setFormError(
        t('diary.resourceNeeded', {
          type: chosenType.name,
          kind: word(t, KIND_WORD, missingKind),
        }),
      )
      return
    }
    if (moving === null && chosen === null) {
      setFormError(t('diary.customerEmpty'))
      return
    }
    if (chosenMinute === null) {
      setFormError(t('diary.pickATime'))
      return
    }
    if (note.length > TEXT_MAX) {
      setFormError(t('diary.noteTooLong'))
      return
    }
    const at = slots?.slots.find((slot) => slot.startMinute === chosenMinute)
    if (at === undefined) {
      setFormError(t('diary.pickATime'))
      return
    }
    if (moving !== null && leadOf(moving) <= 0) {
      // Measured again here, because the row was drawn when the start was still ahead.
      setFormError(t('refusal.appointmentAlreadyStarted'))
      return
    }
    setFormError(null)
    const time = clock(at.startsAt)
    const write = (await enqueue(
      moving === null
        ? {
            method: 'POST',
            path: '/appointments',
            label: t('diary.bookQueueLabel', { name: chosen?.name ?? '', time }),
            body: {
              branchId,
              customerId: chosen?.id ?? '',
              typeId,
              resourceIds: queryIds,
              date: day,
              startMinute: chosenMinute,
              ...(note.trim() === '' ? {} : { notes: note }),
            },
          }
        : {
            method: 'POST',
            path: `/appointments/${encodeURIComponent(moving.id)}/reschedule`,
            label: t('diary.bookQueueLabel', {
              // Never the empty branch: a move is not offered on a row whose name is withheld,
              // because `place` would refuse that customer with a 404.
              name: moving.customerName ?? '',
              time,
            }),
            // No `resourceIds`: absent keeps the ones it was booked with, which is what a move
            // means, and sending them again is a second chance to send one since archived.
            body: {
              expectedVersion: moving.version,
              date: day,
              startMinute: chosenMinute,
            },
          },
    )) as { id: string }
    setWriteId(write.id)
  }, [
    branchId,
    chosen,
    chosenMinute,
    chosenType,
    clock,
    day,
    enqueue,
    leadOf,
    missingKind,
    moving,
    note,
    open,
    queryIds,
    slots,
    t,
    typeId,
  ])

  const change = useCallback(
    async (row: AppointmentRow, path: 'attendance' | 'no-show' | 'cancellation') => {
      const lead = leadOf(row)
      if (path === 'cancellation' && lead <= 0) {
        setRowError(t('refusal.appointmentAlreadyStarted'))
        return
      }
      if (path === 'no-show' && lead > 0) {
        setRowError(t('refusal.appointmentNotStarted'))
        return
      }
      if (path === 'cancellation' && reason.length > TEXT_MAX) {
        setRowError(t('diary.noteTooLong'))
        return
      }
      setRowError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/appointments/${encodeURIComponent(row.id)}/${path}`,
        label: t('queue.subject.appointment'),
        body: {
          expectedVersion: row.version,
          ...(path === 'cancellation' && reason.trim() !== '' ? { reason } : {}),
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, leadOf, reason, t],
  )

  const filterName =
    bookable?.resources.find((resource) => resource.id === resourceFilter)?.name ?? resourceFilter

  const daySurface: SurfaceState =
    loadError !== null
      ? loadError.status === 403
        ? { kind: 'denied' }
        : { kind: 'error', message: t('diary.loadFailed'), onRetry: () => void refresh() }
      : rows === null
        ? { kind: 'loading' }
        : rows.length === 0
          ? resourceFilter === ''
            ? { kind: 'empty' }
            : { kind: 'filtered' }
          : rows.some((row) => row.customerName === null)
            ? { kind: 'partial', missing: t('diary.missingNames') }
            : { kind: 'ready' }

  const bookableSurface: SurfaceState =
    bookableError !== null
      ? {
          kind: 'error',
          message: t('diary.bookablesFailed'),
          onRetry: () => setBookableEpoch((epoch) => epoch + 1),
        }
      : bookable === null
        ? { kind: 'loading' }
        : bookable.types.length === 0
          ? { kind: 'empty' }
          : bookable.resources.length === 0 &&
              bookable.types.some((type) => type.requiredResourceKinds.length > 0)
            ? { kind: 'partial', missing: t('diary.bookablesMissingResources') }
            : { kind: 'ready' }

  /** Which of the empty days this is. Four situations rendered as one blank list is the defect. */
  const noSlotsSentence = (): string => {
    if (slots?.reason === 'RESOURCE_KIND_MISSING' && chosenType !== undefined) {
      return t('diary.resourceNeeded', {
        type: chosenType.name,
        kind: word(t, KIND_WORD, chosenType.requiredResourceKinds[0] ?? ''),
      })
    }
    const key = slots?.reason === null ? undefined : SLOT_REASON[slots?.reason ?? '']
    return key === undefined ? t('diary.slotsNone', { date: dayText }) : t(key)
  }

  const slotSurface: SurfaceState =
    slotsError !== null
      ? slotsError.code === 'APPOINTMENT_SCHEDULE_MISSING'
        ? { kind: 'empty' }
        : {
            kind: 'error',
            message: t('diary.slotsFailed'),
            onRetry: () => setSlotEpoch((epoch) => epoch + 1),
          }
      : slots === null
        ? { kind: 'loading' }
        : slots.slots.length === 0
          ? { kind: 'empty' }
          : { kind: 'ready' }

  const customerSurface: SurfaceState = matchesFailed
    ? {
        kind: 'error',
        message: t('diary.customerFailed'),
        onRetry: () => setSearchEpoch((epoch) => epoch + 1),
      }
    : query.trim().length < SEARCH_FLOOR
      ? { kind: 'empty' }
      : matches === null
        ? { kind: 'loading' }
        : matches.length === 0
          ? { kind: 'filtered' }
          : { kind: 'ready' }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.appointment'))

  return (
    <section className="lm-screen" aria-labelledby="diary-heading">
      <h2 id="diary-heading" className="lm-screen__heading">
        {t('diary.heading')}
      </h2>

      {/* Outside the surface, so the day can still be moved when the day failed to read. */}
      <div className="lm-toolbar">
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => setDay((was) => addCalendarDays(was, -1))}
        >
          {t('diary.previousDay')}
        </button>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => setDay(branchDate(now(), timezone))}
        >
          {t('diary.today')}
        </button>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => setDay((was) => addCalendarDays(was, 1))}
        >
          {t('diary.nextDay')}
        </button>

        {bookable === null || bookable.resources.length === 0 ? null : (
          <div className="lm-field lm-field--inline">
            <label className="lm-field__label" htmlFor="diary-who">
              {t('diary.whoLabel')}
            </label>
            <select
              id="diary-who"
              className="lm-input"
              value={resourceFilter}
              onChange={(e) => setResourceFilter(e.currentTarget.value)}
            >
              <option value="">{t('diary.whoEveryone')}</option>
              {bookable.resources.map((resource) => (
                <option key={resource.id} value={resource.id}>
                  {resource.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {rowError === null ? null : (
        <p className="lm-error" role="alert">
          {rowError}
        </p>
      )}

      <DataSurface
        state={daySurface}
        shape="rows"
        rows={4}
        what={t('diary.what')}
        emptyMessage={t(canBook ? 'diary.emptyCanBook' : 'diary.empty', { date: dayText })}
        filteredMessage={t('diary.filtered', { who: filterName, date: dayText })}
        deniedMessage={t('diary.deniedBranch')}
      >
        <ul className="lm-appointment-list">
          {(rows ?? []).map((row) => {
            const lead = leadOf(row)
            const blocked = moveBlockedBy(row)
            return (
              <li key={row.id} className="lm-appointment">
                <span className="lm-appointment__when">
                  {sentence(t, 'diary.when', {
                    from: <Ltr>{clock(row.startsAt)}</Ltr>,
                    till: <Ltr>{clock(row.endsAt)}</Ltr>,
                  })}
                </span>
                <span className="lm-appointment__who" dir="auto">
                  {row.customerName ?? t('diary.customerUnnamed')}
                </span>
                <span className="lm-appointment__what" dir="auto">
                  {row.typeName}
                </span>
                <span className="lm-appointment__what" dir="auto">
                  {row.resources.length === 0
                    ? t('diary.withNobody')
                    : row.resources.map((resource) => resource.name).join(', ')}
                </span>
                <span className="lm-chip">{statusOf(row)}</span>

                {row.rescheduledFromId === undefined ? null : (
                  <span className="lm-appointment__moved">{t('diary.movedFrom')}</span>
                )}
                {row.rescheduledToId === undefined ? null : (
                  <span className="lm-appointment__moved">{t('diary.movedTo')}</span>
                )}
                {row.status === 'no_show' ? (
                  // Read by somebody about to telephone, so it says where the number is.
                  <span className="lm-appointment__moved">{t('diary.missedHint')}</span>
                ) : null}

                {row.notes === undefined ? null : (
                  <p className="lm-appointment__note">
                    <span className="lm-field__label">{t('diary.noteHeading')}</span>
                    <span dir="auto">{row.notes}</span>
                  </p>
                )}

                {blocked === undefined ? null : (
                  <span className="lm-appointment__moved">{t(blocked)}</span>
                )}

                {canBook && row.status === 'booked' ? (
                  <>
                    {/* `completeAppointment` gates on status and version only, so a customer who
                        arrived early can be recorded before their start time. */}
                    <button
                      type="button"
                      className="lm-button lm-button--primary"
                      disabled={busy}
                      onClick={() => void change(row, 'attendance')}
                    >
                      {t('diary.markAttended')}
                    </button>

                    {lead <= 0 ? (
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        disabled={busy}
                        onClick={() => void change(row, 'no-show')}
                      >
                        {t('diary.markMissed')}
                      </button>
                    ) : (
                      <>
                        {blocked === undefined && row.customerName !== null ? (
                          <button
                            type="button"
                            className="lm-button lm-button--secondary"
                            disabled={busy}
                            onClick={() => {
                              closeForm()
                              setConfirming(null)
                              setMoving(row)
                              setTypeId(row.typeId)
                              setResourceIds(row.resourceIds)
                            }}
                          >
                            {t('diary.reschedule')}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="lm-button lm-button--secondary"
                          disabled={busy}
                          onClick={() => {
                            // Exclusive with the booking form, so "Keep it" names one thing at a
                            // time: the time this appointment has, or the booking not made.
                            closeForm()
                            setRowError(null)
                            setReason('')
                            setConfirming(row.id)
                          }}
                        >
                          {t('diary.cancel')}
                        </button>
                      </>
                    )}
                  </>
                ) : null}

                {confirming === row.id ? (
                  <div className="lm-appointment__confirm">
                    <div className="lm-field">
                      <label className="lm-field__label" htmlFor="diary-cancel-reason">
                        {t('diary.cancelReasonLabel')}
                      </label>
                      <input
                        id="diary-cancel-reason"
                        className="lm-input"
                        dir="auto"
                        autoComplete="off"
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className="lm-button lm-button--danger"
                      disabled={busy}
                      onClick={() => void change(row, 'cancellation')}
                    >
                      {t('diary.cancelConfirm')}
                    </button>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setConfirming(null)
                        setReason('')
                      }}
                    >
                      {t('diary.keep')}
                    </button>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      </DataSurface>

      {/* The booking half. Absent without `customer_create_edit`, because every write below it
          would be a 403 and a 403 stops the shop taking payment. Absent too when the branch
          itself was refused: the day above has already said why, and this would say it worse. */}
      {canBook && bookableError?.status !== 403 ? (
        <section className="lm-form" aria-labelledby="diary-book-heading">
          <h3 id="diary-book-heading" className="lm-screen__subheading">
            {t('diary.bookHeading')}
          </h3>

          <DataSurface
            state={bookableSurface}
            shape="rows"
            rows={2}
            what={t('diary.bookablesWhat')}
            emptyMessage={t(canConfigure ? 'diary.bookablesEmptyOwner' : 'diary.bookablesEmpty')}
          >
            <>
              {moving === null && !booking ? (
                <button
                  type="button"
                  className="lm-button lm-button--primary"
                  onClick={() => {
                    setConfirming(null)
                    setBooking(true)
                  }}
                >
                  {t('diary.book')}
                </button>
              ) : null}

              {open ? (
                <>
                  {moving === null ? (
                    <>
                      {chosen === null ? (
                        <>
                          <div className="lm-field">
                            {/* The label names the field and the hint says how to fill it in. The
                                hint is outside the label element: inside it, the accessible name
                                becomes both sentences and `getByLabelText` stops finding it. */}
                            <label className="lm-field__label" htmlFor="diary-customer">
                              {t('diary.customerLabel')}
                            </label>
                            <input
                              id="diary-customer"
                              className="lm-input"
                              dir="auto"
                              autoComplete="off"
                              value={query}
                              onChange={(e) => setQuery(e.currentTarget.value)}
                            />
                            <p className="lm-hint">{t('diary.customerSearchLabel')}</p>
                          </div>

                          <DataSurface
                            state={customerSurface}
                            shape="rows"
                            rows={3}
                            what={t('diary.customerWhat')}
                            emptyMessage={t('diary.customerEmpty')}
                            filteredMessage={t('diary.customerNone')}
                          >
                            <ul className="lm-matches">
                              {(matches ?? []).map((candidate) => (
                                <li key={candidate.id}>
                                  <button
                                    type="button"
                                    className="lm-match"
                                    onClick={() => {
                                      setChosen({ id: candidate.id, name: candidate.name })
                                      setFormError(null)
                                    }}
                                  >
                                    <span className="lm-match__name" dir="auto">
                                      {candidate.name}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </DataSurface>
                        </>
                      ) : (
                        <p className="lm-hint">
                          {sentence(t, 'diary.chosenCustomer', {
                            name: <span dir="auto">{chosen.name}</span>,
                          })}
                        </p>
                      )}

                      <div className="lm-field">
                        <label className="lm-field__label" htmlFor="diary-type">
                          {t('diary.typeLabel')}
                        </label>
                        <select
                          id="diary-type"
                          className="lm-input"
                          value={typeId}
                          onChange={(e) => {
                            setTypeId(e.currentTarget.value)
                            setFormError(null)
                          }}
                        >
                          <option value="">{t('diary.typeUnset')}</option>
                          {(bookable?.types ?? []).map((type) => (
                            <option key={type.id} value={type.id}>
                              {`${type.name} · ${t('diary.typeDuration', {
                                minutes: type.durationMinutes,
                              })}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {(bookable?.resources ?? []).length === 0 ? null : (
                        <fieldset className="lm-fieldset">
                          <legend className="lm-field__label">{t('diary.resourcesLabel')}</legend>
                          {(bookable?.resources ?? []).map((resource) => (
                            <label className="lm-field lm-field--inline" key={resource.id}>
                              <span className="lm-field__label">
                                {`${resource.name} · ${word(t, KIND_WORD, resource.kind)}`}
                              </span>
                              <input
                                type="checkbox"
                                checked={queryIds.includes(resource.id)}
                                onChange={(e) => {
                                  const on = e.currentTarget.checked
                                  setResourceIds((was) =>
                                    on
                                      ? [...was, resource.id]
                                      : was.filter((id) => id !== resource.id),
                                  )
                                  setFormError(null)
                                }}
                              />
                            </label>
                          ))}
                        </fieldset>
                      )}
                    </>
                  ) : (
                    <p className="lm-hint">
                      {sentence(t, 'diary.rescheduleHint', {
                        time: <Ltr>{clock(moving.startsAt)}</Ltr>,
                      })}
                    </p>
                  )}

                  {/* The one date control, and the day the list above shows. A booking made for a
                      day nobody is looking at is how a shop books into the wrong Tuesday. */}
                  <div className="lm-field">
                    <label className="lm-field__label" htmlFor="diary-day">
                      {t('diary.dayLabel')}
                    </label>
                    <input
                      id="diary-day"
                      className="lm-input"
                      type="date"
                      value={day}
                      onChange={(e) => {
                        const next = e.currentTarget.value
                        if (next !== '') setDay(next)
                      }}
                    />
                  </div>

                  {missingKind === undefined || chosenType === undefined ? null : (
                    // Answered before the slot read, because the read would come back empty with
                    // `RESOURCE_KIND_MISSING` and the screen already knows which kind is missing.
                    <p className="lm-hint" role="status">
                      {t('diary.resourceNeeded', {
                        type: chosenType.name,
                        kind: word(t, KIND_WORD, missingKind),
                      })}
                    </p>
                  )}

                  {timeTaken ? (
                    <p className="lm-error" role="alert">
                      {t('diary.timeTaken')}
                    </p>
                  ) : null}

                  {typeId === '' || missingKind !== undefined ? null : (
                    <>
                      <p className="lm-field__label">{t('diary.slotsLabel', { date: dayText })}</p>
                      <DataSurface
                        state={slotSurface}
                        shape="rows"
                        rows={3}
                        what={t('diary.slotsWhat')}
                        emptyMessage={
                          slotsError?.code === 'APPOINTMENT_SCHEDULE_MISSING'
                            ? t(
                                canConfigure
                                  ? 'diary.scheduleMissingOwner'
                                  : 'diary.scheduleMissing',
                              )
                            : noSlotsSentence()
                        }
                      >
                        <ul className="lm-slots">
                          {(slots?.slots ?? []).map((slot) => (
                            <li key={slot.startMinute}>
                              <button
                                type="button"
                                className={
                                  chosenMinute === slot.startMinute
                                    ? 'lm-slot lm-slot--chosen'
                                    : 'lm-slot'
                                }
                                aria-pressed={chosenMinute === slot.startMinute}
                                onClick={() => void pickTime(slot.startMinute)}
                              >
                                <Ltr>{clock(slot.startsAt)}</Ltr>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </DataSurface>

                      {/* The sentence claims three things, so all three are checked: the time it
                          holds now and the quarter hour either side of it are none of them on
                          offer. Only on its own day, because the move form can be pointed at
                          another one and the claim would then be about the wrong date. */}
                      {moving !== null &&
                      day === moving.localDate &&
                      (slots?.slots.length ?? 0) > 0 &&
                      ![
                        moving.startMinute - 15,
                        moving.startMinute,
                        moving.startMinute + 15,
                      ].some((minute) =>
                        (slots?.slots ?? []).some((slot) => slot.startMinute === minute),
                      ) ? (
                        <p className="lm-hint">{t('diary.rescheduleNoNearby')}</p>
                      ) : null}
                    </>
                  )}

                  {moving === null ? (
                    <div className="lm-field">
                      <label className="lm-field__label" htmlFor="diary-note">
                        {t('diary.noteLabel')}
                      </label>
                      <textarea
                        id="diary-note"
                        className="lm-input lm-input--area"
                        dir="auto"
                        value={note}
                        onChange={(e) => setNote(e.currentTarget.value)}
                      />
                    </div>
                  ) : null}

                  {/* `OFF-005`: never the word "held" before the server has said so. */}
                  <p className="lm-hint">{t('diary.pendingHold')}</p>

                  {formError === null ? null : (
                    <p className="lm-error" role="alert">
                      {formError}
                    </p>
                  )}

                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    disabled={busy}
                    onClick={() => void submit()}
                  >
                    {t('diary.book')}
                  </button>
                  <button type="button" className="lm-button lm-button--secondary" onClick={closeForm}>
                    {t('diary.keep')}
                  </button>
                </>
              ) : null}
            </>
          </DataSurface>
        </section>
      ) : null}

      <p className="lm-queue-state" role="status">
        {line}
      </p>
    </section>
  )
}
